// Copyright (c) 2026 Finerty Data Ltd. All rights reserved.
//
// This file is part of proprietary software owned by Finerty Data Ltd.
// It is confidential and may not be copied, distributed, modified, or used,
// in whole or in part, without the prior written consent of Finerty Data Ltd.
//
// Author: Ahmed Hosny

import { useEffect, useLayoutEffect, useRef, useState } from 'react';
import { createPortal } from 'react-dom';
import { AppIcon } from './AppIcon';
import { DropdownSearchInput } from './DropdownSearchInput';
import { TagChip } from './TagChip';
import './TagFilterSelect.css';

export interface TagFilterOption {
  id: string;
  name: string;
  color: string;
  archived?: boolean;
}

export interface TagFilterSelectProps {
  options: TagFilterOption[];
  /** Currently-selected tag ids (ANY-of semantics, PRD FR-13). */
  selectedIds: string[];
  onChange: (next: string[]) => void;
  /** Trigger label; defaults to "Tags". */
  label?: string;
  /** Renders a search box above the option list when there are many tags. Defaults to true when options.length >= 8. */
  searchable?: boolean;
  placeholder?: string;
  disabled?: boolean;
  className?: string;
  'data-testid'?: string;
}

/**
 * Multi-select filter control matching the existing filter-popover styling
 * used by the Request Overview card — a button trigger that opens a
 * checkbox-list popover rendered into a document.body portal (so it floats
 * above table clipping contexts). Selecting/deselecting fires `onChange`
 * immediately; the caller owns the actual list refetch.
 */
export function TagFilterSelect({
  options,
  selectedIds,
  onChange,
  label = 'Tags',
  searchable,
  placeholder = 'Search tags',
  disabled = false,
  className = '',
  'data-testid': dataTestId,
}: TagFilterSelectProps) {
  const [open, setOpen] = useState(false);
  const [search, setSearch] = useState('');
  const [position, setPosition] = useState<{ top: number; left: number } | null>(null);
  const triggerRef = useRef<HTMLButtonElement>(null);
  const popoverRef = useRef<HTMLDivElement>(null);

  const showSearch = searchable ?? options.length >= 8;

  useLayoutEffect(() => {
    if (!open) return;
    const updatePosition = () => {
      const rect = triggerRef.current?.getBoundingClientRect();
      if (!rect) return;
      setPosition({ top: rect.bottom + 4, left: rect.left });
    };
    updatePosition();
    window.addEventListener('resize', updatePosition);
    window.addEventListener('scroll', updatePosition, true);
    return () => {
      window.removeEventListener('resize', updatePosition);
      window.removeEventListener('scroll', updatePosition, true);
    };
  }, [open]);

  useEffect(() => {
    if (!open) return;
    const handleClickOutside = (e: MouseEvent) => {
      const target = e.target as Node;
      if (
        popoverRef.current &&
        !popoverRef.current.contains(target) &&
        triggerRef.current &&
        !triggerRef.current.contains(target)
      ) {
        setOpen(false);
      }
    };
    document.addEventListener('mousedown', handleClickOutside);
    return () => document.removeEventListener('mousedown', handleClickOutside);
  }, [open]);

  const toggleOption = (id: string) => {
    if (selectedIds.includes(id)) onChange(selectedIds.filter((v) => v !== id));
    else onChange([...selectedIds, id]);
  };

  const visibleOptions = showSearch && search.trim()
    ? options.filter((o) => o.name.toLowerCase().includes(search.trim().toLowerCase()))
    : options;

  const triggerClass = [
    'fin-tag-filter__trigger',
    selectedIds.length > 0 && 'fin-tag-filter__trigger--active',
  ].filter(Boolean).join(' ');

  const popover = open && position ? createPortal(
    <div
      ref={popoverRef}
      className="fin-tag-filter__popover"
      role="listbox"
      style={{ top: position.top, left: position.left }}
      data-testid={dataTestId ? `${dataTestId}-popover` : undefined}
    >
      {showSearch && (
        <div className="fin-tag-filter__search">
          <DropdownSearchInput
            value={search}
            onChange={setSearch}
            placeholder={placeholder}
            ariaLabel={placeholder}
          />
        </div>
      )}
      <div className="fin-tag-filter__options">
        {visibleOptions.length === 0 ? (
          <span className="fin-tag-filter__empty">No tags found</span>
        ) : (
          visibleOptions.map((opt) => (
            <label key={opt.id} className="fin-tag-filter__option">
              <input
                type="checkbox"
                checked={selectedIds.includes(opt.id)}
                onChange={() => toggleOption(opt.id)}
              />
              <TagChip name={opt.name} color={opt.color} archived={opt.archived} />
            </label>
          ))
        )}
      </div>
      {selectedIds.length > 0 && (
        <button type="button" className="fin-tag-filter__clear" onClick={() => onChange([])}>
          Clear
        </button>
      )}
    </div>,
    document.body,
  ) : null;

  return (
    <div className={`fin-tag-filter ${className}`.trim()}>
      <button
        ref={triggerRef}
        type="button"
        className={triggerClass}
        onClick={() => setOpen((o) => !o)}
        disabled={disabled}
        aria-haspopup="listbox"
        aria-expanded={open}
        aria-label={`Filter by ${label}`}
        data-testid={dataTestId}
      >
        {label}
        {selectedIds.length > 0 && ` (${selectedIds.length})`}
        <AppIcon name="chevron down" size={14} />
      </button>
      {popover}
    </div>
  );
}
